import {Capacitor} from "@capacitor/core";
import {Haptics, ImpactStyle, NotificationType} from "@capacitor/haptics";

/**
 * Тактильная обратная связь (вибрация).
 *
 * ## Философия — не «вибрация на каждый тап»
 *
 * Отклик даём только на **смену состояния, которую пользователь не заказывал
 * явно или ждёт как результат**: мэтч, отправленное сообщение, оплата, ошибка,
 * отпускание свайпа. Никаких вибраций на обычные тапы, навигацию, скролл и
 * pull-to-refresh. Пере-обратная связь обесценивает всю остальную: если телефон
 * жужжит постоянно, пользователь перестаёт различать сигналы.
 *
 * ## Три транспорта, в порядке приоритета
 *
 * 1. **Capacitor Haptics plugin** — при обёртке `web/android/` (Capacitor
 *    Android wrapper). Мапит наши стили один-в-один на нативный Vibrator/
 *    HapticFeedbackConstants, независимо от того, поддерживает ли WebView
 *    `navigator.vibrate`. Основной путь для APK.
 * 2. **Legacy RN-мост** (`window.ReactNativeWebView`) — старая обёртка
 *    `mobile-app/`. Отправляем типизированное JSON-сообщение, нативка вызывает
 *    системный хаптик. Единственный путь к нормальной тактильной отдаче на iOS
 *    в той обёртке: `navigator.vibrate` в WKWebView не существует и никогда не
 *    существовал — это решение Apple, а не пробел здесь.
 * 3. **`navigator.vibrate`** — обычный браузер (Android Chrome). На iOS Safari
 *    тихий no-op благодаря feature-detection.
 *
 * ### Почему legacy-мост включается флагом, а не наличием `ReactNativeWebView`
 *
 * Мост используем ТОЛЬКО когда нативка сама объявила `window.__RL_NATIVE_HAPTICS__
 * = true` (см. `mobile-app/App.tsx`, `injectedJavaScriptBeforeContentLoaded`).
 * Причина — совместимость версий: веб обновляется мгновенно, а APK у людей на
 * телефонах старый. Если бы мы переключались на мост по одному лишь наличию
 * `ReactNativeWebView`, то у всех со старой сборкой сообщения уходили бы в
 * никуда и вибрация на Android **пропала бы совсем**. Флаг ставит только та
 * сборка, которая умеет его обработать; все остальные тихо остаются на
 * `navigator.vibrate`. Двойного срабатывания при этом тоже нет — транспорт
 * всегда ровно один.
 *
 * ## prefers-reduced-motion сознательно НЕ выключает вибрацию
 *
 * Это осознанное решение, а не недосмотр. `prefers-reduced-motion` — про
 * вестибулярный дискомфорт от **визуального** движения (параллакс, крупные
 * перелёты, зумы); вибрация его не вызывает. Более того, для человека, который
 * приглушил анимацию, хаптик остаётся единственным невизуальным подтверждением
 * действия — выключив и его, мы оставили бы такого пользователя с наименьшей
 * обратной связью из всех. Управление вибрацией — отдельная сущность: у неё
 * есть свой переключатель в настройках (ниже) и системный переключатель ОС
 * (iOS: «Звуки, тактильные сигналы», Android: режим вибрации), который в любом
 * случае действует ниже нашего слоя.
 */

/**
 * Словарь тактильных стилей. Намеренно совпадает с системными стилями iOS
 * (UIFeedbackGenerator), чтобы нативная сторона была тонким маппингом без
 * собственных решений о силе отклика.
 *
 * Шкала по возрастанию заметности:
 * - `selection`    — щелчок на грани восприятия; частые действия;
 * - `impactLight`  — лёгкий, но ощутимый толчок; осознанное действие;
 * - `impactMedium` — плотный толчок; действие «со стоимостью»;
 * - `success` / `warning` / `error` — **итоги**, а не действия. Держим в резерве:
 *   как только «успехом» отмечается каждый чих, настоящий успех перестаёт читаться.
 */
export type HapticStyle =
    | "selection"
    | "impactLight"
    | "impactMedium"
    | "success"
    | "warning"
    | "error";

/** Сообщение, которое понимает `mobile-app/App.tsx`. Меняя формат — правьте обе стороны. */
export type HapticBridgeMessage = {
    type: "haptic";
    style: HapticStyle;
};

/**
 * Аппроксимация стилей на `navigator.vibrate`. Android умеет только «сколько
 * миллисекунд гудеть», поэтому итоги (success/warning/error) отличаем ритмом:
 * одиночный импульс = действие, узор = результат.
 */
const WEB_PATTERNS: Record<HapticStyle, number | number[]> = {
    selection: 6,
    impactLight: 12,
    impactMedium: 20,
    success: [12, 60, 24],
    warning: [20, 70, 20],
    error: [30, 90, 30],
};

/** Ключ пользовательской настройки. Осознанно локальный — см. `isHapticsEnabled`. */
export const HAPTICS_STORAGE_KEY = "rl.haptics.enabled";

/**
 * Включена ли вибрация. Настройка **локальная для устройства**, а не в аккаунте:
 * вибромотор — свойство конкретного телефона, а не человека. Планшет без мотора,
 * рабочий ноутбук и личный телефон не должны делить один флаг, и наоборот —
 * выключив вибрацию в метро, пользователь не ожидает, что она пропадёт на всех
 * его устройствах. По умолчанию включено.
 *
 * Значение не кэшируем: `localStorage` синхронный и дешёвый, а кэш пришлось бы
 * инвалидировать между вкладками.
 */
export function isHapticsEnabled(): boolean {
    try {
        if (typeof localStorage === "undefined") return true;
        return localStorage.getItem(HAPTICS_STORAGE_KEY) !== "0";
    } catch {
        // Приватный режим / отключённое хранилище — не повод терять отклик.
        return true;
    }
}

/** Сохранить настройку вибрации на этом устройстве. */
export function setHapticsEnabled(enabled: boolean): void {
    try {
        if (typeof localStorage === "undefined") return;
        localStorage.setItem(HAPTICS_STORAGE_KEY, enabled ? "1" : "0");
    } catch {
        // Не смогли сохранить — переключатель отработает в пределах сессии.
    }
}

type NativeHapticsBridge = { postMessage: (message: string) => void };

/** Мост нативной обёртки, если текущая сборка объявила поддержку хаптиков. */
function nativeBridge(): NativeHapticsBridge | null {
    if (typeof window === "undefined") return null;
    const scope = window as typeof window & {
        __RL_NATIVE_HAPTICS__?: unknown;
        ReactNativeWebView?: { postMessage?: unknown };
    };
    if (scope.__RL_NATIVE_HAPTICS__ !== true) return null;
    const bridge = scope.ReactNativeWebView;
    if (!bridge || typeof bridge.postMessage !== "function") return null;
    return bridge as NativeHapticsBridge;
}

function vibrate(pattern: number | number[]): void {
    try {
        if (typeof navigator !== "undefined" && "vibrate" in navigator) {
            navigator.vibrate(pattern);
        }
    } catch {
        // Некоторые браузеры бросают в нетипичных контекстах (например, без
        // недавнего user gesture) — вибрация не критична для потока, тихо игнорируем.
    }
}

/**
 * Единственная точка, где вибрация реально запускается: сначала настройка, потом
 * ровно один транспорт.
 *
 * `webPattern` — необязательное переопределение узора для `navigator.vibrate`,
 * когда у события есть исторически сложившийся «почерк» (см. `hapticQrScanned`).
 */
function fire(style: HapticStyle, webPattern?: number | number[]): void {
    if (!isHapticsEnabled()) return;

    if (capacitorHapticsFire(style)) return;

    const bridge = nativeBridge();
    if (bridge) {
        const message: HapticBridgeMessage = {type: "haptic", style};
        try {
            bridge.postMessage(JSON.stringify(message));
        } catch {
            // Мост есть, но отправка не удалась — молча, вибрация не критична.
        }
        return;
    }

    vibrate(webPattern ?? WEB_PATTERNS[style]);
}

/**
 * Capacitor Haptics transport. Плагин доступен только на нативной платформе;
 * в браузере `Capacitor.isNativePlatform()` вернёт false и мы прозрачно упадём
 * на следующий транспорт (RN-мост или navigator.vibrate). Импорт статический,
 * чтобы первое срабатывание не ждало динамической загрузки — задержка
 * «действие → отклик» больше 50мс уже разрывает связку в восприятии.
 */
function capacitorHapticsFire(style: HapticStyle): boolean {
    if (typeof window === "undefined") return false;
    if (!Capacitor.isNativePlatform()) return false;
    try {
        switch (style) {
            case "selection":
                void Haptics.selectionStart();
                break;
            case "impactLight":
                void Haptics.impact({style: ImpactStyle.Light});
                break;
            case "impactMedium":
                void Haptics.impact({style: ImpactStyle.Medium});
                break;
            case "success":
                void Haptics.notification({type: NotificationType.Success});
                break;
            case "warning":
                void Haptics.notification({type: NotificationType.Warning});
                break;
            case "error":
                void Haptics.notification({type: NotificationType.Error});
                break;
        }
    } catch {
        // Плагин недоступен на этой сборке — не критично, тишина.
    }
    return true;
}

/* ────────────────────────────  Словарь событий  ────────────────────────────
 * Ниже — единственный разрешённый способ вызвать вибрацию из приложения.
 * Места вызова не выбирают силу отклика: они называют событие, а соответствие
 * «событие → стиль» живёт здесь, в одном месте, и потому остаётся связным.
 */

/** Взаимная симпатия — главный положительный ИТОГ приложения. */
export function hapticMatch(): void {
    fire("success");
}

/**
 * Успешно отсканирован QR-код. Узор на вебе сохранён историческим ([15,60,15]),
 * чтобы на ощупь отличался от мэтча; на iOS это плотный одиночный толчок «поймал».
 */
export function hapticQrScanned(): void {
    fire("impactMedium", [15, 60, 15]);
}

/**
 * Свайп вправо (лайк) отпущен и карточка ушла. Осознанное положительное
 * действие — лёгкий, но ощутимый толчок.
 */
export function hapticSwipeLike(): void {
    fire("impactLight");
}

/**
 * Свайп влево (пропуск). Самое частое действие в приложении — сотни раз за
 * сессию. Держим на грани восприятия: всё, что заметнее, за десять минут
 * превращается в назойливый шум и обесценивает лайк на контрасте.
 */
export function hapticSwipeDislike(): void {
    fire("selection");
}

/**
 * Особая симпатия отправлена. Платное действие — подтверждаем сам факт траты
 * плотным толчком. Заметнее обычного лайка, но легче мэтча: это ещё не итог,
 * а только отправка.
 */
export function hapticSuperLikeSent(): void {
    fire("impactMedium");
}

/**
 * Сообщение ушло на сервер. Действие частое и ожидаемое, поэтому — щелчок на
 * грани восприятия, а не «успех»: узор успеха на каждом сообщении в живой
 * переписке был бы невыносим.
 */
export function hapticMessageSent(): void {
    fire("selection");
}

/** Платёж подтверждён — итог, которого ждали. */
export function hapticPaymentSuccess(): void {
    fire("success");
}

/** Платёж не прошёл — итог, требующий внимания. */
export function hapticPaymentFailed(): void {
    fire("error");
}

/**
 * Ошибка, о которой сообщаем пользователю (в т.ч. невалидная форма).
 * Подключено централизованно — см. `utils/toastHaptics.ts`.
 */
export function hapticError(): void {
    fire("error");
}

/**
 * Образец отклика при включении вибрации в настройках. Переключатель вибрации —
 * единственный контрол, результат которого нельзя показать глазами: даём
 * почувствовать ровно то, что включаем.
 */
export function hapticPreview(): void {
    fire("impactLight");
}

/** Едва заметный импульс — долгое нажатие открыло контекстное действие (например, удаление сообщения). */
export function hapticLongPress(): void {
    vibrate(12);
}
